import { useCallback, useEffect, useMemo, useRef } from 'react';
import { ScheduleRequest } from '@weave/schema-gen-ts/dist/schemas/schedule/api/v2/api.pb';
import { uniqBy } from 'lodash-es';
import type { OverlappingCalendarEvent, ScheduleRequestEvent } from '@frontend/schedule-helpers';
import { createShallowStore, createStore } from '@frontend/store';
import { sentry } from '@frontend/tracking';
import { useCalendarDeepLinkParams } from '../hooks/calendar/use-calendar-deep-link-params';
import { convertScheduleRequestEventToRequest } from '../hooks/scheduler-v3/use-convert-schedule-request-to-event';
import {
  useGetSelectedCalendarDateAppointmentInfo,
  useGetSelectedCalendarDateAppointmentList,
} from '../hooks/use-get-selected-calendar-date-appointment-info';
import { ExtendedAppointment } from '../types';

export type AppointmentDetailsEntryPoint = 'schedule' | 'schedule_activity' | 'patient_profile';

type State = {
  appointmentDetailsEntryPoint: AppointmentDetailsEntryPoint;
  insuranceStatusCalendarDate?: string;
  selectedAppointments: ExtendedAppointment[];
  selectedAppointment?: ExtendedAppointment;
  selectedAppointmentId?: string;
  selectedRequests: ScheduleRequest[];
  selectedRequest?: ScheduleRequest;
  selectedRequestId?: string;
  /** Event IDs highlighted on the calendar grid. Kept in the store so
   *  `resetSelection` can clear panel state and grid highlight atomically. */
  selectedEventIds: string[];
  /** True when `selectedEventIds` is set but appointments haven't been flushed into
   *  `selectedAppointments` yet. Stored in the global store (not a local ref) so it
   *  survives CalendarV2/V3WithGrid remounts during view switches or deep-link arrivals. */
  hasPendingSync: boolean;
  /** Tracks the deep-linked event ID that has already triggered a highlight, so the
   *  deep-link fires once per URL value and survives remounts without re-opening the panel. */
  activatedDeepLinkEventId: string | undefined;
};

type Actions = {
  setSelectedAppointments: (appointments: OverlappingCalendarEvent[], insuranceStatusCalendarDate: string) => void;
  updateSelectedAppointment: (appointment: ExtendedAppointment) => void;
  setSelectedAppointment: (
    appointment: ExtendedAppointment,
    reset?: boolean,
    entryPoint?: AppointmentDetailsEntryPoint
  ) => void;
  setSelectedAppointmentId: (appointmentId: string, entryPoint?: AppointmentDetailsEntryPoint) => void;
  resetSelectedAppointment: () => void;
  resetSelection: () => void;
  setSelectedRequestId: (requestId: string) => void;
  resetSelectedRequest: () => void;
  resetRequests: () => void;
  setSelectedRequest: (request: ScheduleRequest, reset?: boolean) => void;
  setSelectedEventIds: (eventIds: string[]) => void;
  /** Highlights event IDs on the grid without opening the info panel. Used for
   *  deep-link arrivals — the panel only opens when the user clicks the highlighted card. */
  setDeepLinkedEventIds: (eventIds: (string | undefined)[]) => void;
  setActivatedDeepLinkEventId: (eventId: string | undefined) => void;
};

const initialState: State = {
  appointmentDetailsEntryPoint: 'schedule',
  insuranceStatusCalendarDate: undefined,
  selectedAppointments: [],
  selectedRequests: [],
  selectedEventIds: [],
  hasPendingSync: false,
  activatedDeepLinkEventId: undefined,
};

type ScheduleAppointmentInfoPanelStore = State & Actions;

const useStore = createStore<ScheduleAppointmentInfoPanelStore>(
  (set) => ({
    ...initialState,
    setSelectedAppointments: (appointments, insuranceStatusCalendarDate) => {
      const requests = uniqBy(
        (appointments.filter((appt) => appt.type === 'request') as ScheduleRequestEvent[])
          .map((app) => convertScheduleRequestEventToRequest(app.request))
          .filter(Boolean) as ScheduleRequest[],
        'id'
      );
      const selectedAppointments = appointments.filter((appt) => appt.type !== 'request');

      set({
        appointmentDetailsEntryPoint: 'schedule',
        insuranceStatusCalendarDate,
        selectedAppointments,
        // auto select appointment if only one is selected
        selectedAppointment: selectedAppointments.length === 1 ? selectedAppointments[0] : undefined,
        selectedAppointmentId: selectedAppointments.length === 1 ? selectedAppointments[0].id : undefined,
        selectedRequests: requests,
        selectedRequest: requests.length === 1 ? requests[0] : undefined,
        selectedRequestId: requests.length === 1 ? requests[0].id : undefined,
        hasPendingSync: false,
      });
    },
    updateSelectedAppointment: (appointment) => set({ selectedAppointment: appointment }),
    setSelectedAppointment: (appointment, reset = false, entryPoint = 'schedule') => {
      if (reset) {
        set({
          appointmentDetailsEntryPoint: entryPoint,
          selectedAppointment: appointment,
          selectedAppointmentId: appointment.id,
          selectedAppointments: [],
          insuranceStatusCalendarDate: undefined,
        });
      } else {
        set({
          appointmentDetailsEntryPoint: entryPoint,
          selectedAppointment: appointment,
          selectedAppointmentId: appointment.id,
        });
      }
    },
    setSelectedRequest: (request, reset = false) => {
      if (reset) {
        set({
          selectedRequest: request,
          selectedRequestId: request.id,
          selectedRequests: [],
        });
      } else {
        set({ selectedRequest: request, selectedRequestId: request.id });
      }
    },
    setSelectedAppointmentId: (appointmentId, entryPoint = 'schedule') => {
      set({
        appointmentDetailsEntryPoint: entryPoint,
        selectedAppointmentId: appointmentId,
        selectedAppointment: undefined,
        selectedAppointments: [],
        insuranceStatusCalendarDate: undefined,
      });
    },
    setSelectedRequestId: (requestId) => {
      set({
        selectedRequestId: requestId,
        selectedRequest: undefined,
        selectedRequests: [],
      });
    },
    resetSelectedAppointment: () =>
      set({
        appointmentDetailsEntryPoint: 'schedule',
        selectedAppointment: undefined,
        selectedAppointmentId: undefined,
      }),
    resetSelection: () =>
      set({
        appointmentDetailsEntryPoint: 'schedule',
        selectedAppointments: [],
        selectedAppointment: undefined,
        selectedAppointmentId: undefined,
        insuranceStatusCalendarDate: undefined,
        selectedRequests: [],
        selectedRequest: undefined,
        selectedRequestId: undefined,
        selectedEventIds: [],
        hasPendingSync: false,
      }),
    resetRequests: () => set({ selectedRequests: [], selectedRequest: undefined, selectedRequestId: undefined }),
    resetSelectedRequest: () => set({ selectedRequest: undefined, selectedRequestId: undefined }),
    setSelectedEventIds: (eventIds) => set({ selectedEventIds: eventIds, hasPendingSync: true }),
    // Does NOT set hasPendingSync — grid highlight only, panel stays closed.
    setDeepLinkedEventIds: (eventIds) => set({ selectedEventIds: eventIds.filter((id) => id !== undefined) }),
    setActivatedDeepLinkEventId: (eventId) => set({ activatedDeepLinkEventId: eventId }),
  }),
  {
    name: 'ScheduleAppointmentInfoPanelStore',
    enabled: true,
    trace: true,
  }
);

export const useScheduleAppointmentInfoPanelShallowStore = createShallowStore(useStore);

export const useSetCalendarAppointmentInfoAsSelected = (
  eventId: string,
  locationId: string,
  cachedAppointments?: ExtendedAppointment[],
  entryPoint: AppointmentDetailsEntryPoint = 'schedule'
) => {
  const {
    selectedAppointment,
    selectedAppointmentId,
    updateSelectedAppointment,
    setSelectedAppointment,
    setSelectedAppointmentId,
    resetSelection,
  } = useScheduleAppointmentInfoPanelShallowStore(
    'selectedAppointment',
    'selectedAppointmentId',
    'updateSelectedAppointment',
    'setSelectedAppointment',
    'setSelectedAppointmentId',
    'resetSelection'
  );
  // get appointment list based on calendar context & cached queries
  const apptInfoFromCalendar = useGetSelectedCalendarDateAppointmentInfo(eventId);

  // Memoize apptInfoFromCache to prevent creating new object references on every render
  const apptInfoFromCache = useMemo(() => {
    if (apptInfoFromCalendar) {
      return apptInfoFromCalendar;
    }
    return cachedAppointments?.find((appt) => appt.id === eventId);
    // SP-TODO: Find better way to handle deps here
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(apptInfoFromCalendar), JSON.stringify(cachedAppointments), eventId]);

  const isSelected = selectedAppointmentId === eventId;

  // need to sync selected appointment info if selected to keep data consistent on updates outside of this store
  useEffect(() => {
    if (isSelected && apptInfoFromCache) {
      updateSelectedAppointment(apptInfoFromCache);
    }
  }, [apptInfoFromCache, isSelected, updateSelectedAppointment]);

  return {
    isSelected,
    selectedAppointment,
    setAppointment: (reset?: boolean) => {
      if (apptInfoFromCache) {
        setSelectedAppointment(apptInfoFromCache, reset, entryPoint);
      } else {
        console.error('[useSetCalendarAppointmentInfoAsSelected] appointment info not found.');
        sentry.error({
          topic: 'schedule-calendar',
          team: 'schedule',
          error: `Appointment info not found.`,
          addContext: { name: 'details', context: { eventId, locationId } },
        });
        setSelectedAppointmentId(eventId, entryPoint);
      }
    },
    resetSelection,
  };
};

export const useSetCalendarAppointmentListAsSelected = (eventIds: string[], calendarDate: string) => {
  const { setSelectedAppointments, selectedAppointments } = useScheduleAppointmentInfoPanelShallowStore(
    'setSelectedAppointments',
    'selectedAppointments'
  );
  // get appointment list based on calendar context & cached queries
  const appointments = useGetSelectedCalendarDateAppointmentList(eventIds);

  const selectedAppointmentIds = useMemo(() => selectedAppointments.map((appt) => appt.id), [selectedAppointments]);

  const hasSelectedAppointmentsInList = useMemo(
    () => eventIds.some((eventId) => selectedAppointmentIds.includes(eventId)),
    [eventIds, selectedAppointmentIds]
  );

  const shouldSyncSelection = selectedAppointmentIds?.length !== eventIds?.length;

  // on resize of calendar view, sync current overlapping events
  useEffect(() => {
    if (hasSelectedAppointmentsInList && shouldSyncSelection) {
      setSelectedAppointments(appointments, calendarDate);
    }
  }, [appointments, hasSelectedAppointmentsInList, shouldSyncSelection, setSelectedAppointments, calendarDate]);

  const setAppointments = useCallback(
    () => setSelectedAppointments(appointments, calendarDate),
    [setSelectedAppointments, appointments, calendarDate]
  );

  return {
    isSelected: hasSelectedAppointmentsInList,
    selectedAppointments,
    setAppointments,
  };
};

/**
 * Wires calendar grid selection (V2/V3) to the info panel store. Pass `handleEventsSelect`
 * as the `onEventsSelect` prop. Handles flush-to-store, deep-link highlight, and the
 * double-flush guard that prevents both grid instances from flushing simultaneously.
 */
export const useSetCalendarGridEventsAsSelected = (calendarDate: string) => {
  // Mutex ref: whichever CalendarV2/V3WithGrid instance first observes hasPendingSync=true
  // acquires the flush; the other skips. Reset when hasPendingSync returns to false.
  const isFlushingRef = useRef(false);

  const {
    setSelectedAppointments,
    selectedEventIds,
    setSelectedEventIds,
    setDeepLinkedEventIds,
    hasPendingSync,
    activatedDeepLinkEventId,
    setActivatedDeepLinkEventId,
  } = useScheduleAppointmentInfoPanelShallowStore(
    'setSelectedAppointments',
    'selectedEventIds',
    'setSelectedEventIds',
    'setDeepLinkedEventIds',
    'hasPendingSync',
    'activatedDeepLinkEventId',
    'setActivatedDeepLinkEventId'
  );
  const { deepLinkedEventId } = useCalendarDeepLinkParams();

  // Resolve full appointment objects for the current selectedEventIds from cached queries
  const appointments = useGetSelectedCalendarDateAppointmentList(selectedEventIds);

  // Flush resolved appointments into the store once hasPendingSync is true and data is ready.
  // isFlushingRef prevents both CalendarV2/V3WithGrid instances from flushing simultaneously.
  useEffect(() => {
    if (hasPendingSync && appointments.length && !isFlushingRef.current) {
      isFlushingRef.current = true;
      setSelectedAppointments(appointments, calendarDate);
      // setSelectedAppointments resets hasPendingSync in the store, re-triggering this effect;
      // the else branch then resets isFlushingRef so the next selection can proceed.
    } else if (!hasPendingSync) {
      isFlushingRef.current = false;
    }
  }, [appointments, calendarDate, hasPendingSync, setSelectedAppointments]);

  // Highlight the deep-linked event once per unique URL value using setDeepLinkedEventIds
  // (not setSelectedEventIds) so hasPendingSync is NOT set and the panel stays closed.
  // activatedDeepLinkEventId persists in the store across remounts and is not reset on panel close.
  useEffect(() => {
    if (activatedDeepLinkEventId !== deepLinkedEventId) {
      setActivatedDeepLinkEventId(deepLinkedEventId);
      setDeepLinkedEventIds([deepLinkedEventId]);
    }
  }, [deepLinkedEventId, activatedDeepLinkEventId, setActivatedDeepLinkEventId, setDeepLinkedEventIds]);

  // `setSelectedEventIds` already sets hasPendingSync: true in the store action.
  const handleEventsSelect = useCallback(
    (eventIds: string[]) => {
      setSelectedEventIds(eventIds);
    },
    [setSelectedEventIds]
  );

  return { handleEventsSelect, selectedEventIds, deepLinkedEventId };
};
